/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import {
	getCanvasMusicVideoSceneDataService,
	getCanvasMusicVideoSceneService,
	getStudioMusicVideoSceneDataService,
	getStudioMusicVideoSceneService,
} from 'api/services/IntermediateMediaService';
import { AmplitudeEvents } from 'core/constants/AmplitudeEvents';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { GenerationType } from 'core/constants/enums/GenerationType.ts';
import { MusicVideoGenerateActions } from 'core/constants/enums/MusicVideoGenerateActions.ts';
import { MusicVideoGenerationProcess } from 'core/constants/enums/MusicVideoGenerationProcess.ts';
import { RendernetErrors } from 'core/constants/enums/RendernetErrors.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { PostHogEvents } from 'core/constants/PostHogEvents';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { STUDIO } from 'core/navigation/Routes';
import { captureAmplitudeEvent } from 'core/utils/AmplitudeUtils.ts';
import { LoggerUtil } from 'core/utils/loggerUtil.ts';
import { capturePosthogEvent } from 'core/utils/PosthogUtil.ts';
import { addCharacterNameToText } from 'core/utils/utils';

const resetMusicVideoIntermediateData = (state, mediaId) => {
	const musicVideoScenesListCopy = { ...state.musicVideoScenesList };
	delete musicVideoScenesListCopy[mediaId];

	const musicVideoAspectRatioMapCopy = { ...state.musicVideoAspectRatioMap };
	delete musicVideoAspectRatioMapCopy[mediaId];

	const musicVideoAudioDataMapCopy = { ...state.musicVideoAudioDataMap };
	delete musicVideoAudioDataMapCopy[mediaId];

	return {
		musicVideoScenesList: musicVideoScenesListCopy,
		musicVideoAspectRatioMap: musicVideoAspectRatioMapCopy,
		musicVideoAudioDataMap: musicVideoAudioDataMapCopy,
	};
};
export const musicVideoUpdateScenesAsync = createAsyncThunk(
	'musicVideo/generateMusicVideoScenes',
	async (data, { getState }) => {
		const { generateParams } = data;
		const { canvasId } = getState().canvas;

		const queryParams = canvasId ? { canvas_id: canvasId } : {};
		const request = {
			generation_type: GenerationType.MUSIC_VIDEO,
			process: MusicVideoGenerationProcess.GENERATE_CHARACTER,
			action: MusicVideoGenerateActions.RE_SUBMIT,
			input: generateParams.inputValue,
		};

		if (canvasId) {
			return getCanvasMusicVideoSceneService(generateParams.mediaId, queryParams, request);
		}
		return getStudioMusicVideoSceneService(generateParams.mediaId, queryParams, request);
	},
);

export const getMusicVideoSceneDataAsync = createAsyncThunk(
	'musicVideo/generateMusicVideoData',
	async ({ mediaId }, { getState }) => {
		const { canvasId } = getState().canvas;
		const queryParams = canvasId ? { canvas_id: canvasId } : {};

		if (canvasId) {
			return getCanvasMusicVideoSceneDataService(mediaId, queryParams);
		}
		return getStudioMusicVideoSceneDataService(mediaId, queryParams);
	},
);

export const getMusicVideoAsync = createAsyncThunk(
	'musicVideo/generateMusicVideo',
	async (data, { getState }) => {
		const { canvasId } = getState().canvas;
		const { generateParams, route } = data;

		const queryParams = canvasId ? { canvas_id: canvasId } : {};
		const request = {
			generation_type: GenerationType.MUSIC_VIDEO,
			process: MusicVideoGenerationProcess.GENERATE_VIDEO,
			action: MusicVideoGenerateActions.START,
			input: {},
		};

		if (route === STUDIO) {
			return getCanvasMusicVideoSceneService(generateParams.mediaId, queryParams, request);
		}
		return getStudioMusicVideoSceneService(generateParams.mediaId, queryParams, request);
	},
);

// Initial state
const initialState = {
	musicVideoScenesData: {},
	musicVideoScenesList: {},
	musicVideoAspectRatioMap: {},
	musicVideoAudioDataMap: {},
	musicVideoUpdateSceneApiStage: ApiStage.IDLE,
	musicVideoGenerateApiStage: ApiStage.IDLE,
};

export const intermediateMediaInitialState = initialState;

const intermediateMediaSlice = createSlice({
	name: 'intermediateMedia',
	initialState,
	reducers: {
		setMusicVideoScenesData: (state, action) => {
			state.musicVideoScenesData = action.payload;
		},
		setMusicVideoScenesList: (state, action) => {
			state.musicVideoScenesList = action.payload;
		},
		setMusicVideoAspectRatioMap: (state, action) => {
			state.musicVideoAspectRatioMap = action.payload;
		},
		setMusicVideoAudioDataMap: (state, action) => {
			state.musicVideoAudioDataMap = action.payload;
		},
		resetIntermediateState: (state) => {
			Object.keys(initialState).forEach((key) => {
				state[key] = initialState[key];
			});
		},
	},
	extraReducers: (builder) => {
		builder.addCase(musicVideoUpdateScenesAsync.pending, (state) => {
			state.musicVideoUpdateSceneApiStage = ApiStage.LOADING;
		});
		builder.addCase(musicVideoUpdateScenesAsync.fulfilled, (state, action) => {
			const { generateParams } = action.meta.arg;
			const scenes = generateParams.inputValue;
			const scenesDataCopy = { ...state.musicVideoScenesData };
			Object.keys(scenes).forEach((key) => {
				delete scenesDataCopy[key];
			});
			const { data } = action.payload ?? {};
			const nextScenes = data?.scenes ?? [];
			const mediaId = data?.id;
			const nextSceneIdsList = [];
			nextScenes.forEach((nextScene) => {
				scenesDataCopy[nextScene.id] = {
					url: nextScene.url,
					status: nextScene.status,
					character: nextScene.character?.id,
					prompt: addCharacterNameToText(nextScene?.prompt, nextScene?.character?.name),
					mediaId,
				};
				nextSceneIdsList.push(nextScene.id);
			});
			state.musicVideoScenesData = scenesDataCopy;
			state.musicVideoScenesList = {
				...state.musicVideoScenesList,
				[mediaId]: [...nextSceneIdsList],
			};

			state.musicVideoUpdateSceneApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(musicVideoUpdateScenesAsync.rejected, (state, action) => {
			state.musicVideoUpdateSceneApiStage = ApiStage.ERROR;
			const error = action.error.code;
			LoggerUtil.logError('[Generate musicVideoUpdateScenes Media]: Error', action?.error);
			LoggerUtil.logInfo('[Generate musicVideoUpdateScenes Media]: Parameters', action?.meta?.arg);

			if (error === RendernetErrors.NSFW_PROMPT) {
				captureAmplitudeEvent(AmplitudeEvents.NSFW_TOAST);
				capturePosthogEvent(PostHogEvents.NSFW_TOAST);
				sendToast(ToastType.NSFW_CONTENT);
			} else if (
				error !== RendernetErrors.INVALID_CREDENTIALS &&
				error !== RendernetErrors.BLOCKED_USER &&
				error !== RendernetErrors.INSUFFICIENT_CREDITS
			) {
				sendToast(ToastType.GENERATE_API_ERROR);
			}
		});

		// get scenes data
		builder.addCase(getMusicVideoSceneDataAsync.pending, (state) => {
			state.musicVideoSceneApiStage = ApiStage.LOADING;
		});
		builder.addCase(getMusicVideoSceneDataAsync.fulfilled, (state, action) => {
			state.musicVideoSceneApiStage = ApiStage.SUCCESS;
			const data = action.payload;

			if (data && data.scenes) {
				const { mediaId, scenes, aspectRatio, audioDetails } = data;

				state.musicVideoScenesData = {
					...state.musicVideoScenesData,
					...scenes.reduce((acc, scene) => {
						acc[scene.id] = {
							url: scene.url || '',
							status: scene.status || 'pending',
							character: scene.character?.id || null,
							prompt: addCharacterNameToText(scene.prompt, scene.character?.name),
							mediaId,
							...state.musicVideoScenesData[scene.id],
						};
						return acc;
					}, {}),
				};

				state.musicVideoScenesList = {
					...state.musicVideoScenesList,
					[mediaId]: scenes.map((scene) => scene.id),
				};
				state.musicVideoAspectRatioMap = {
					...state.musicVideoAspectRatioMap,
					[mediaId]: aspectRatio,
				};
				state.musicVideoAudioDataMap = {
					...state.musicVideoAudioDataMap,
					[mediaId]: audioDetails,
				};
			}
		});

		builder.addCase(getMusicVideoSceneDataAsync.rejected, (state, action) => {
			state.musicVideoSceneApiStage = ApiStage.ERROR;
			state.musicVideoSceneApiStage = ApiStage.ERROR;
			const error = action.error.code;
			LoggerUtil.logError('[Generate musicVideoUpdateScenes Media]: Error', action?.error);
			LoggerUtil.logInfo('[Generate musicVideoUpdateScenes Media]: Parameters', action?.meta?.arg);

			if (error === RendernetErrors.NSFW_PROMPT) {
				captureAmplitudeEvent(AmplitudeEvents.NSFW_TOAST);
				capturePosthogEvent(PostHogEvents.NSFW_TOAST);
				sendToast(ToastType.NSFW_CONTENT);
			} else if (
				error !== RendernetErrors.INVALID_CREDENTIALS &&
				error !== RendernetErrors.BLOCKED_USER &&
				error !== RendernetErrors.INSUFFICIENT_CREDITS
			) {
				sendToast(ToastType.GENERATE_API_ERROR);
			}
		});

		builder.addCase(getMusicVideoAsync.pending, (state) => {
			state.musicVideoGenerateApiStage = ApiStage.LOADING;
		});
		builder.addCase(getMusicVideoAsync.fulfilled, (state, action) => {
			state.musicVideoGenerateApiStage = ApiStage.SUCCESS;
			const { data } = action.payload;
			const { id: mediaId } = data;
			const updateState = resetMusicVideoIntermediateData(state, mediaId);
			state.musicVideoScenesList = updateState.musicVideoScenesList;
			state.musicVideoAspectRatioMap = updateState.musicVideoAspectRatioMap;
			state.musicVideoAudioDataMap = updateState.musicVideoAudioDataMap;
		});
		builder.addCase(getMusicVideoAsync.rejected, (state, action) => {
			state.musicVideoGenerateApiStage = ApiStage.ERROR;
			const { generateParams } = action.meta.arg;
			const { mediaId } = generateParams;
			const updateState = resetMusicVideoIntermediateData(state, mediaId);
			state.musicVideoScenesList = updateState.musicVideoScenesList;
			state.musicVideoAspectRatioMap = updateState.musicVideoAspectRatioMap;
			state.musicVideoAudioDataMap = updateState.musicVideoAudioDataMap;
		});
	},
});

// Export actions
export const {
	setMusicVideoScenesData,
	setMusicVideoScenesList,
	setMusicVideoAspectRatioMap,
	setMusicVideoAudioDataMap,
	resetIntermediateState,
} = intermediateMediaSlice.actions;

// Export selectors
export const musicVideoScenesDataSelector = (state) =>
	state.intermediateMedia?.musicVideoScenesData;
export const musicVideoScenesListSelector = (state) =>
	state.intermediateMedia?.musicVideoScenesList;
export const MusicVideoAudioDataMapSelector = (state) =>
	state.intermediateMedia?.musicVideoAudioDataMap;
export const musicVideoUpdateSceneApiStageSelector = (state) =>
	state.intermediateMedia?.musicVideoUpdateSceneApiStage;
export const musicVideoGenerateApiStageSelector = (state) =>
	state.intermediateMedia?.musicVideoGenerateApiStage;
export const musicVideoScenesDataApiStageSelector = (state) =>
	state.intermediateMedia?.musicVideoSceneApiStage;
export const musicVideoScenesListByMediaIdSelector = createSelector(
	[musicVideoScenesListSelector, (state, mediaId) => mediaId],
	(musicVideoScenesList, mediaId) => {
		return musicVideoScenesList?.[mediaId] ?? [];
	},
);

// Export reducer
export default intermediateMediaSlice.reducer;
